import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Trash2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { Modal, FieldLabel, TextInput, PrimaryButton, GhostButton } from '@/components/watchlist/Modal';
import { ReferencePhotoUpload } from '@/components/watchlist/ReferencePhotoUpload';
import { CandidateScoreResult } from '@/components/watchlist/CandidateScoreResult';
import { EntityGraphPanel } from '@/components/watchlist/EntityGraphPanel';
import { wl, classificationColor } from '@/components/watchlist/theme';
import type { VIPProfileRecord, CandidateScore } from '@/types/watchlist';

interface HandleRow { platform: string; handle: string }

interface FormState {
  name: string;
  aliases: string;
  bioKeywords: string;
  knownAssociates: string;
  walletAddresses: string;
  handles: HandleRow[];
}

function recordToForm(r?: VIPProfileRecord): FormState {
  return {
    name: r?.name ?? '',
    aliases: r?.aliases.join(', ') ?? '',
    bioKeywords: r?.bio_keywords.join(', ') ?? '',
    knownAssociates: r?.known_associates.join(', ') ?? '',
    walletAddresses: r?.known_wallet_addresses.join(', ') ?? '',
    handles: r ? Object.entries(r.official_handles).map(([platform, handle]) => ({ platform, handle })) : [{ platform: 'twitter', handle: '' }],
  };
}

function CreateEditModal({ initial, onClose, onSaved }: { initial?: VIPProfileRecord; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(recordToForm(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateHandle(i: number, patch: Partial<HandleRow>) {
    setForm((f) => ({ ...f, handles: f.handles.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) }));
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const official_handles: Record<string, string> = {};
      for (const h of form.handles) if (h.platform.trim() && h.handle.trim()) official_handles[h.platform.trim()] = h.handle.trim();
      const body = {
        name: form.name.trim(),
        aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
        official_handles,
        bio_keywords: form.bioKeywords.split(',').map((s) => s.trim()).filter(Boolean),
        known_associates: form.knownAssociates.split(',').map((s) => s.trim()).filter(Boolean),
        known_wallet_addresses: form.walletAddresses.split(',').map((s) => s.trim()).filter(Boolean),
        is_active: true,
      };
      if (initial) await watchlistApi.updateVipProfile(initial.id, body);
      else await watchlistApi.createVipProfile(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? 'Edit VIP Profile' : 'New VIP Profile'} onClose={onClose} maxWidth={620} accent={wl.info}>
      <div className="flex flex-col gap-4">
        <div>
          <FieldLabel>Name</FieldLabel>
          <TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Executive" />
        </div>
        <div>
          <FieldLabel>Aliases (comma-separated)</FieldLabel>
          <TextInput value={form.aliases} onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Bio keywords (comma-separated — phrases a legit page about them might use)</FieldLabel>
          <TextInput value={form.bioKeywords} onChange={(e) => setForm((f) => ({ ...f, bioKeywords: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Known associates (comma-separated)</FieldLabel>
          <TextInput value={form.knownAssociates} onChange={(e) => setForm((f) => ({ ...f, knownAssociates: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Known wallet addresses ("chain:address", comma-separated)</FieldLabel>
          <TextInput value={form.walletAddresses} onChange={(e) => setForm((f) => ({ ...f, walletAddresses: e.target.value }))} placeholder="ethereum:0x..." />
        </div>
        <div>
          <FieldLabel>Official handles per platform</FieldLabel>
          <div className="flex flex-col gap-2">
            {form.handles.map((h, i) => (
              <div key={i} className="flex gap-2 items-center">
                <TextInput style={{ width: 120 }} value={h.platform} onChange={(e) => updateHandle(i, { platform: e.target.value })} placeholder="twitter" />
                <TextInput style={{ flex: 1 }} value={h.handle} onChange={(e) => updateHandle(i, { handle: e.target.value })} placeholder="@realjaneexec" />
                <button onClick={() => setForm((f) => ({ ...f, handles: f.handles.filter((_, idx) => idx !== i) }))} style={{ color: wl.textMuted }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setForm((f) => ({ ...f, handles: [...f.handles, { platform: '', handle: '' }] }))}
            className="flex items-center gap-1.5 mt-2"
            style={{ color: wl.info, fontSize: 12 }}
          >
            <Plus size={13} /> Add platform
          </button>
        </div>

        {error && <p style={{ color: wl.critical, fontSize: 12 }}>{error}</p>}

        <div className="flex justify-end gap-2 mt-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ScoringPanel({ profile }: { profile: VIPProfileRecord }) {
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [platform, setPlatform] = useState('twitter');
  const [bio, setBio] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [result, setResult] = useState<CandidateScore | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runScore() {
    if (!handle.trim() || !displayName.trim()) { setError('Candidate handle and display name are required.'); return; }
    setRunning(true);
    setError(null);
    try {
      const candidate_photo_base64 = photo ? await fileToBase64(photo) : undefined;
      const score = await watchlistApi.scoreImpersonation({
        profile_type: 'vip', profile_id: profile.id,
        candidate_handle: handle.trim(), candidate_display_name: displayName.trim(),
        candidate_platform: platform, candidate_bio: bio.trim() || undefined,
        candidate_photo_base64,
      });
      setResult(score);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scoring failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <p style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>SCORE A CANDIDATE ACCOUNT</p>
        <div className="flex flex-col gap-2.5">
          <div className="flex gap-2">
            <TextInput style={{ flex: 1 }} value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="Candidate handle" />
            <TextInput style={{ width: 110 }} value={platform} onChange={(e) => setPlatform(e.target.value)} placeholder="platform" />
          </div>
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Candidate display name" />
          <TextInput value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Candidate bio (optional)" />
          <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} style={{ color: wl.textMuted, fontSize: 11.5 }} />
          <div>
            <PrimaryButton onClick={runScore} disabled={running}>{running ? 'Scoring…' : 'Score candidate'}</PrimaryButton>
          </div>
          {error && <p style={{ color: wl.critical, fontSize: 12 }}>{error}</p>}
        </div>
      </div>
      <div>
        {result ? <CandidateScoreResult score={result} /> : (
          <p style={{ color: wl.textFaint, fontSize: 12, marginTop: 24 }}>Results appear here.</p>
        )}
      </div>
    </div>
  );
}

function DetectedCandidatesPanel({ profileId }: { profileId: string }) {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const query = useQuery({
    queryKey: ['watchlist-vip-detected-candidates', profileId, showAll],
    queryFn: () => watchlistApi.listVipDetectedCandidates(profileId, !showAll),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700 }}>
          AUTO-DETECTED CANDIDATES (background scan, every 60 min)
        </p>
        <label className="flex items-center gap-1.5" style={{ color: wl.textFaint, fontSize: 11 }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          show every account checked
        </label>
      </div>
      {query.isLoading ? (
        <Loader2 className="animate-spin" size={16} style={{ color: wl.info }} />
      ) : (query.data?.candidates.length ?? 0) === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 12 }}>No candidates found yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {query.data!.candidates.map((c) => {
            const effectiveClassification = c.overridden_classification ?? c.classification;
            return (
            <div key={c.id} className="flex flex-col gap-2 py-2.5 px-3" style={{ background: wl.bg, border: `1px solid ${wl.border}`, borderRadius: 8 }}>
              <div className="flex items-center justify-between">
                <div>
                  <span style={{ color: wl.text, fontSize: 12.5, fontWeight: 600 }}>@{c.candidate_handle}</span>
                  <span style={{ color: wl.textMuted, fontSize: 11, marginLeft: 8 }}>{c.platform}</span>
                  {c.overridden_classification && <span style={{ color: wl.textFaint, fontSize: 10, marginLeft: 8 }}>(overridden)</span>}
                </div>
                <div className="text-right">
                  <span style={{ color: classificationColor(effectiveClassification), fontSize: 11, fontWeight: 700 }}>
                    {effectiveClassification.toUpperCase()}
                  </span>
                  <span style={{ color: wl.textFaint, fontSize: 10.5, marginLeft: 6 }}>{(c.risk_score * 100).toFixed(0)}% risk</span>
                </div>
              </div>
              <CandidateScoreResult
                score={c}
                override={{ profileId, candidateId: c.id, onOverridden: () => qc.invalidateQueries({ queryKey: ['watchlist-vip-detected-candidates', profileId] }) }}
              />
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function VipsPanel() {
  const qc = useQueryClient();
  const [modalFor, setModalFor] = useState<'new' | VIPProfileRecord | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['watchlist-vip-profiles'], queryFn: () => watchlistApi.listVipProfiles() });

  const deleteMut = useMutation({
    mutationFn: (id: string) => watchlistApi.deleteVipProfile(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist-vip-profiles'] }),
  });

  function refetch() {
    qc.invalidateQueries({ queryKey: ['watchlist-vip-profiles'] });
  }

  return (
    <div>
      {modalFor && (
        <CreateEditModal initial={modalFor === 'new' ? undefined : modalFor} onClose={() => setModalFor(null)} onSaved={refetch} />
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 style={{ color: wl.text, fontWeight: 800, fontSize: 15 }}>VIP profiles</h2>
          <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>Face-match + handle impersonation scoring</p>
        </div>
        <PrimaryButton onClick={() => setModalFor('new')}>+ New VIP profile</PrimaryButton>
      </div>

      {query.isLoading ? (
        <Loader2 className="animate-spin" size={22} style={{ color: wl.info }} />
      ) : (query.data?.vip_profiles.length ?? 0) === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 13 }}>No VIP profiles yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {query.data!.vip_profiles.map((profile) => (
            <motion.div key={profile.id} layout style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10 }}>
              <div className="flex items-center justify-between px-5 py-4">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded(expanded === profile.id ? null : profile.id)}>
                  <span style={{ color: wl.text, fontWeight: 700, fontSize: 14 }}>{profile.name}</span>
                  <p style={{ color: wl.textMuted, fontSize: 11.5, marginTop: 3 }}>
                    {Object.keys(profile.official_handles).length} handle(s) · {profile.reference_photo_asset_ids.length} reference photo(s) ·{' '}
                    {profile.known_wallet_addresses.length} wallet(s)
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <GhostButton onClick={() => setModalFor(profile)}>Edit</GhostButton>
                  <GhostButton destructive onClick={() => confirm(`Delete "${profile.name}"?`) && deleteMut.mutate(profile.id)}>
                    <Trash2 size={13} />
                  </GhostButton>
                  <button onClick={() => setExpanded(expanded === profile.id ? null : profile.id)} style={{ color: wl.textMuted }}>
                    {expanded === profile.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              {expanded === profile.id && (
                <div className="px-5 pb-5 flex flex-col gap-5" style={{ borderTop: `1px solid ${wl.border}`, paddingTop: 16 }}>
                  <ReferencePhotoUpload
                    label="Reference photos"
                    assetIds={profile.reference_photo_asset_ids}
                    onUpload={async (file) => {
                      const updated = await watchlistApi.uploadVipPhoto(profile.id, file);
                      refetch();
                      return updated.reference_photo_asset_ids[updated.reference_photo_asset_ids.length - 1];
                    }}
                    onDelete={async (assetId) => {
                      await watchlistApi.deleteVipPhoto(profile.id, assetId);
                      refetch();
                    }}
                  />
                  {profile.entity_id && <EntityGraphPanel entityId={profile.entity_id} />}
                  <ScoringPanel profile={profile} />
                  <DetectedCandidatesPanel profileId={profile.id} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
